import { usePostHog } from "posthog-js/react"
import { useEffect, useMemo, useState } from "react"

import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { isObjectsEqual } from "~/ui-rtk/utils/object-utils"
import type { ConnectorItemProps } from "./components"
import {
  isConnectorExcluded,
  isVisibleToCompany,
  mapConnectorStatus,
  mapConnectorStatusWithVersionCheck,
  isAirbyteConnector,
  isCustomConnector,
  pickWorstStatusConnection,
} from "~/ui-rtk/utils/connector-utils"
import { isConnectorVersionUpToDate } from "~/ui-rtk/utils/connector-version-utils"
import {
  CategoryConnectorGroup,
  ConnectorGroupItem,
} from "~/ui-rtk/pages/Onboarding/ConnectSources/types"
import { sortArrayWithEssentialFirst } from "./utils"
import {
  ConnectionStatus,
  ConnectorMetadata,
  ReleaseCycle,
} from "~/ui-rtk/api/types"
import useConnectedServices from "~/ui-rtk/hooks/connected-services"
import useConnectors from "~/ui-rtk/hooks/connectors"
import useMentionsReviewAccess from "~/ui-rtk/hooks/mentions-review-access"
import useGoogleConnectorAccess from "~/ui-rtk/hooks/google-connector-access"
import {
  CONNECTORS_IDS,
  CONNECTORS_IDS_GROUPED,
} from "~/ui-rtk/constants/sources"

export type ConnectorItem = Pick<
  ConnectorItemProps,
  | "id"
  | "service"
  | "label"
  | "icon"
  | "status"
  | "releaseCycle"
  | "confirmedAt"
  | "metadata"
>

export const useConnect = () => {
  const [connectors, setConnectors] = useState<ConnectorItem[]>([])
  const hasMentionsReviewAccess = useMentionsReviewAccess()
  const hasGoogleConnectorAccess = useGoogleConnectorAccess()

  const {
    connectedServices,
    refetchConnectors,
    isGetCompanyConnectorsError,
    isGetCompanyConnectorsLoading,
    getCompanyConnectorByService,
  } = useConnectedServices()

  const {
    connectors: dataConnectors,
    isGetAllConnectorsError,
    isGetAllConnectorsLoading,
    getConnectorById,
    connect,
  } = useConnectors()

  const currentCompany = useCurrentCompany()

  const isError = isGetAllConnectorsError || isGetCompanyConnectorsError
  const isLoading = isGetAllConnectorsLoading || isGetCompanyConnectorsLoading

  const posthog = usePostHog()

  const { categories, connectorsGroup } = useMemo(() => {
    const ga4ConnectorIds = CONNECTORS_IDS_GROUPED[CONNECTORS_IDS.GA4]
    const connectorsGroup = dataConnectors.reduce(
      (acc: CategoryConnectorGroup, connector) => {
        // Filter out GA4 connector if feature flag is disabled
        if (
          ga4ConnectorIds.includes(connector.id) &&
          !hasGoogleConnectorAccess
        ) {
          return acc
        }

        connector.category.forEach(category => {
          if (!acc[category]) {
            acc[category] = []
          }

          if (!currentCompany) {
            return
          }

          if (!isVisibleToCompany(connector, currentCompany.id)) {
            return
          }

          // Filter out GA4 from connected services if feature flag is disabled
          const filteredConnectedServices = connectedServices?.filter(
            service => {
              if (
                ga4ConnectorIds.includes(service.serviceName) &&
                !hasGoogleConnectorAccess
              ) {
                return false
              }
              return true
            },
          )

          // For multi-connection connectors, find the worst-status connection
          const connectedServicesForConnector =
            filteredConnectedServices?.filter(
              s => s.serviceName === connector.id,
            ) ?? []

          const connectedService = pickWorstStatusConnection(
            connectedServicesForConnector,
          )

          // Check if connector version is up to date
          const isVersionUpToDate = isConnectorVersionUpToDate(
            connector,
            connectedService,
          )

          const status = mapConnectorStatusWithVersionCheck(
            connectedService?.status,
            hasMentionsReviewAccess,
            isVersionUpToDate,
          )

          const connectorGroupItem: ConnectorGroupItem = {
            id: connector.id,
            companyConnectorId: connectedService?.id,
            hasMultipleConnectors: connectedServicesForConnector.length > 1,
            name: connector.name,
            icon: connector.icon,
            category: connector.category,
            confirmedAt: connectedService?.confirmedAt,
            metadata: {
              ...(connectedService?.metadata || {}),
            } as ConnectorMetadata,
            isBeta: connector.releaseCycle === ReleaseCycle.BETA,
            isAirbyte: isAirbyteConnector(connector),
            isCustom: isCustomConnector(connector),
            status,
            hasVersionMismatch: !isVersionUpToDate,
          }
          acc[category].push(connectorGroupItem)
        })
        return acc
      },
      {},
    )

    if (connectorsGroup["Essential"]) {
      const essentialConnectorCount = connectorsGroup["Essential"]?.reduce(
        (count, service) => {
          if (service.status === ConnectionStatus.CONNECTED) {
            return count + 1
          }
          return count
        },
        0,
      )

      if (currentCompany) {
        // Filter connected services for PostHog tracking
        const ga4ConnectorIds = CONNECTORS_IDS_GROUPED[CONNECTORS_IDS.GA4]
        const filteredConnectedServicesForTracking = (
          connectedServices ?? []
        ).filter(service => {
          if (
            ga4ConnectorIds.includes(service.serviceName) &&
            !hasGoogleConnectorAccess
          ) {
            return false
          }
          return true
        })

        posthog?.group(
          "company",
          currentCompany?.id,
          filteredConnectedServicesForTracking
            .map(connectedService => {
              const key = `${connectedService.serviceName.split(" ").join("_").toLowerCase()}_connector_status`
              const value = mapConnectorStatus(
                connectedService?.status,
                hasMentionsReviewAccess,
              )
              return { [key]: value }
            })
            .reduce((result, obj) => ({ ...result, ...obj }), {
              essential_connector_count: essentialConnectorCount,
            }),
        )
      }
    }

    const categories = sortArrayWithEssentialFirst(Object.keys(connectorsGroup))
    return {
      categories,
      connectorsGroup,
    }
  }, [
    dataConnectors,
    currentCompany,
    connectedServices,
    hasMentionsReviewAccess,
    hasGoogleConnectorAccess,
  ])

  const totalEssentialConnectorsAmount = useMemo(
    () => (connectorsGroup["Essential"] ?? []).length,
    [connectorsGroup],
  )

  const essentialConnectorsConnectedAmount = useMemo(() => {
    if (totalEssentialConnectorsAmount === 0) {
      return 0
    }
    return connectorsGroup["Essential"].filter(
      essentialConnector =>
        essentialConnector.status === ConnectionStatus.CONNECTED ||
        essentialConnector.status === "UPGRADE",
    ).length
  }, [connectorsGroup, totalEssentialConnectorsAmount])

  const essentialConnectorsToReconnectAmount = useMemo(() => {
    if (totalEssentialConnectorsAmount === 0) {
      return 0
    }
    return connectorsGroup["Essential"].filter(
      essentialConnector => essentialConnector.status === "RECONNECT",
    ).length
  }, [connectorsGroup, totalEssentialConnectorsAmount])

  const allEssentialConnectorsEnabled = useMemo(
    () =>
      totalEssentialConnectorsAmount > 0 &&
      essentialConnectorsConnectedAmount === totalEssentialConnectorsAmount,
    [totalEssentialConnectorsAmount, essentialConnectorsConnectedAmount],
  )

  const connectorsMemo = useMemo(() => {
    const ga4ConnectorIds = CONNECTORS_IDS_GROUPED[CONNECTORS_IDS.GA4]
    const filteredConnectors = dataConnectors
      .reduce((acc: ConnectorItem[], connector) => {
        if (isConnectorExcluded(connector.id)) {
          return acc
        }

        // Filter out GA4 if feature flag is disabled
        if (
          ga4ConnectorIds.includes(connector.id) &&
          !hasGoogleConnectorAccess
        ) {
          return acc
        }

        if (
          currentCompany &&
          isVisibleToCompany(connector, currentCompany.id)
        ) {
          // Filter connected services for this connector
          const filteredConnectedServices = connectedServices?.filter(
            service => {
              if (
                ga4ConnectorIds.includes(service.serviceName) &&
                !hasGoogleConnectorAccess
              ) {
                return false
              }
              return true
            },
          )

          // For multi-connection: pick worst-status connection for display
          const connectedServicesForConnector =
            filteredConnectedServices?.filter(
              service => service.serviceName === connector.id,
            ) ?? []
          const connectedService = pickWorstStatusConnection(
            connectedServicesForConnector,
          )

          // Check if connector version is up to date
          const isVersionUpToDate = isConnectorVersionUpToDate(
            connector,
            connectedService,
          )

          acc.push({
            id: connectedService?.id || "",
            service: connector.id,
            label: connector.name,
            icon: connector.icon,
            status: mapConnectorStatusWithVersionCheck(
              connectedService?.status,
              hasMentionsReviewAccess,
              isVersionUpToDate,
            ),
            confirmedAt: connectedService?.confirmedAt,
            releaseCycle: connector.releaseCycle,
            metadata: {
              ...connector.metadata,
              ...(connectedService?.metadata || {}),
            },
          })
        }

        return acc
      }, [])
      .sort((a, _b) => (a.releaseCycle === ReleaseCycle.BETA ? 1 : -1))

    return filteredConnectors
  }, [
    dataConnectors,
    connectedServices,
    currentCompany,
    isGetCompanyConnectorsLoading,
    hasMentionsReviewAccess,
    hasGoogleConnectorAccess,
  ])

  useEffect(() => {
    if (!isObjectsEqual(connectors, connectorsMemo)) {
      setConnectors(connectorsMemo)
    }
  }, [connectorsMemo])

  return {
    categories,
    connectorsGroup,
    connectors,
    totalEssentialConnectorsAmount,
    essentialConnectorsConnectedAmount,
    allEssentialConnectorsEnabled,
    essentialConnectorsToReconnectAmount,
    isError,
    isLoading,
    connect,
    getConnectorById,
    getCompanyConnectorByService,
    refetchConnectors,
  }
}
